
import {
  computed,
  defineComponent,
  h,
  onBeforeMount, PropType, provide,
  reactive,
  ref, shallowReactive,
  Slot, toRaw,
  toRefs, unref,
  useAttrs,
  useSlots,
  VNode,
  watch
} from 'vue'
import { uuid } from '@/utils'
import { Fragment } from 'vue'

import TickIcon from '@/components/Icons/TickIcon.vue'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import { cloneDeep, isEqual } from 'lodash'
import PenIcon from '@/components/Icons/PenIcon.vue'
import { applyChange, diff } from 'deep-diff'
import DButton from '@/components/base/DButton.vue'
import { useQuasar } from 'quasar'

const __default__ = defineComponent({
  name: 'EditableBlock',
  inheritAttrs: false,
  components: { PenIcon, CloseIcon, TickIcon, DButton },
  props: {
    title: {
      type: String,
      default: ''
    },
    modelValue: {
      type: Object,
      default: () => ({})
    },
    editing: {
      type: Boolean,
      default: false,
    },
    columnCount: {
      type: Number,
      default: () => 1,
    },
    rowCount: {
      type: Number,
      default: 4,
    },
    layout: {
      type: String as PropType<'grid' | 'flex-row' | 'flex-col'>,
      default: undefined,
    },
    removeBlockStyle: {
      type: Boolean,
    },
    removeScopeStyle: {
      type: Boolean,
    },
    notify: {
      type: Boolean,
    },
    readonly: {
      type: Boolean,
    },
    saveLabel: {
      type: String,
      default: 'Saved'
    }
  },
  emits: ['update:editing', 'update:model-value', 'cancel', 'save'],
  setup (props, { emit, attrs }) {
    const $q = useQuasar()
    const state = reactive({
      editingInternal: props.editing,
    })

    watch(() => props.editing, (newValue) => state.editingInternal !== newValue && (state.editingInternal = newValue))
    watch(() => state.editingInternal, (newValue) => props.editing !== newValue && emit('update:editing', newValue))

    const editState = reactive({ ...cloneDeep(props.modelValue) })

    watch(
      () => props.modelValue,
      () => {
        if (!props.editing) {
          Object.assign(editState, cloneDeep(props.modelValue))
          Object.keys(editState).filter(k => !(k in props.modelValue)).forEach(k => delete editState[k])
        }
      },
      { deep: true }
    )

    provide('editing', computed(() => state.editingInternal))

    const gridStyling = computed(() => ({
      gridTemplateColumns: 'auto 1fr',
    }))

    const cancel = () => {
      Object.assign(editState, cloneDeep(props.modelValue))
      Object.keys(editState).filter(k => !(k in props.modelValue)).forEach(k => delete editState[k])
      state.editingInternal = false
      emit('cancel')
    }

    // Deep compares two objects and returns an object with the changed entries
    const compareObjects = (obj1, obj2) => {
      const differences = {}
      for (const key in obj1) {
        if (key in obj2) {
          if (Array.isArray(obj1[key]) && Array.isArray(obj2[key])) {
            if (!isEqual(obj1[key], obj2[key])) {
              differences[key] = obj2[key]
            }
          } else if (typeof obj1[key] === 'object' && obj1[key] !== null && typeof obj2[key] === 'object' && obj2[key] !== null) {
            const nestedDiff = compareObjects(obj1[key], obj2[key])
            if (Object.keys(nestedDiff).length > 0) {
              differences[key] = obj2[key]
            }
          } else if (obj1[key] !== obj2[key]) {
            differences[key] = obj2[key]
          }
        } else {
          differences[key] = undefined
        }
      }
      for (const key in obj2) {
        if (!(key in obj1)) {
          differences[key] = obj2[key]
        }
      }
      return differences
    }

    const save = () => {
      const changeProperties = compareObjects(props.modelValue, editState)
      // console.log('changed', changeProperties)
      state.editingInternal = false
      emit('update:model-value', changeProperties)
      if (props.notify) $q.notify({ type: 'positive', message: props.saveLabel, classes: 'custom-notify success-notify' })
    }

    const editingRef = reactive({
      value: computed({
        get: () => state.editingInternal,
        set: (val) => state.editingInternal = val
      })
    })

    return {
      ...toRefs(state),
      gridStyling,
      editState,
      editingRef,
      cancel,
      save,
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "7aa92810": (_ctx.gridStyling.gridTemplateColumns)
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__